// Copyright (c) 2021 WiseTime. All rights reserved.

import React, { useEffect, useMemo, useState } from "react"
import { useSelector } from "react-redux"
import { Loader } from "../../shared/styles"
import WeekDayTabStateIcon from "./WeekDayTabStateIcon"
import { computeTotalPostedSecs, TimelineDate } from "../dateTime"
import * as TimelineSlice from "../slices/timeline"
import * as TimelineStatsSlice from "../slices/stats"
import { usePrevious } from "react-use"
import { RootState } from "../../../app/store"

type Props = {
  isFuture: boolean,
  timelineDate: TimelineDate,
}

const DateTabIndicator = ({ isFuture, timelineDate }: Props): React.ReactElement<Props> | null => {
  // Create a unique instance of a memoized selector
  // https://redux.js.org/usage/deriving-data-selectors#creating-unique-selector-instances
  const makeSelectDaySummaryForDate = useMemo(TimelineStatsSlice.selectDaySummary, [])
  const daySummary = useSelector((state: RootState) => makeSelectDaySummaryForDate(state, timelineDate))
  const activeDate = useSelector(TimelineSlice.selectActiveDate)
  const refreshRequired = useSelector(TimelineSlice.selectRefreshRequired)
  const isLoading = useSelector(TimelineSlice.selectIsLoading)
  const prevActiveDate = usePrevious(activeDate)

  const [showLoader, setShowLoader] = useState(false)

  /**
   * Show the loader spinner on below cases only:
   * 1. When current tab is clicked
   * 2. Current tab shown from direct browser URL
   * 3. Tab is shown when week tab is switched for the first time. (i.e. That day's rows is not already cached)
   */
  useEffect(() => {
    if (timelineDate === activeDate && activeDate === prevActiveDate || timelineDate === activeDate && isLoading) {
      setShowLoader(refreshRequired)
      return
    }
    setShowLoader(false)
  }, [refreshRequired, isLoading])

  if (isFuture) {
    return null
  }

  if (showLoader) {
    return <Loader />
  }

  if (!daySummary) {
    return null
  }

  return (
    <WeekDayTabStateIcon
      totalPosted={computeTotalPostedSecs(daySummary?.timePostedToSiteList)}
      totalUnposted={daySummary.totalUnposted?.seconds || 0}
    />
  )
}

export default DateTabIndicator
